import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Dropdown, Spinner } from '@components';
import UserPermissionTypes from '@constants/permissions';
import { GROUPS } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import {
  Event,
  sendMixpanelEvent,
  trackPageView
} from '@core/analytics/mixpanel';
import { useLazyGetRolesAndPermissionsQuery } from '@features/authentication/queries/AuthQuery';
import useCheckPermission from '@hooks/checkPermission';
import Head from '@layouts/head/Head';
import { getLocalStorageItem } from '@utils/storage';

import BranchRevenueChart from '../components/BranchRevenueChart';
import DashboardCardsOverview from '../components/dashboard-cards-overview/DashboardCardsOverview';
import DashboardDateRangePicker from '../components/dashboard-date-range-picker/DashboardDateRangePicker';
import GroupPerfomanceChart from '../components/GroupPerfomanceChart';
import PaymentLinksAndModes from '../components/PaymentLinksAndModes';
import {
  allBranches,
  DashboardDateRangePickerTypesEnum,
  defaultDateRange,
  paymentDateFilterOptions
} from '../constants';
import { DateRangeType, PaymentDateFilterType } from '../types';

const OrgDashboard = () => {
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);

  const [selectedDateRange, setSelectedDateRange] = useState<{
    dateRange: DateRangeType;
    dateRangeType: DashboardDateRangePickerTypesEnum;
  }>({
    dateRange: defaultDateRange(),
    dateRangeType: DashboardDateRangePickerTypesEnum.thisMonth
  });
  const [selectedFilter, setSelectedFilter] =
    useState<PaymentDateFilterType>('due_at');

  const selectedBranch = allBranches.value;
  const selectedBranchId = `${allBranches.id}`;

  const { currentOrg, currentUser } = useSelector((state: any) => state.user);
  const { checkPermission } = useCheckPermission();

  const [
    getRolesAndPermissions,
    {
      data: rolesAndPermissions,
      isFetching: isPermissionsFetching,
      isSuccess: isPermissionsSuccess
    }
  ] = useLazyGetRolesAndPermissionsQuery();

  const orgId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const navigate = useNavigate();

  const handleDateRangeSelect = (
    dateRange: DateRangeType,
    dateRangeType: DashboardDateRangePickerTypesEnum
  ) => {
    const isSameSelection =
      selectedDateRange.dateRangeType === dateRangeType &&
      selectedDateRange.dateRange.startDate === dateRange.startDate &&
      selectedDateRange.dateRange.endDate === dateRange.endDate;

    if (isSameSelection) return;

    const nextSelection = { dateRange, dateRangeType };
    setSelectedDateRange(nextSelection);
    sendMixpanelEvent(Event.DASHBOARD_SELECTED_DATERANGE, {
      selectedDateRange: nextSelection
    });
  };

  useEffect(() => {
    trackPageView('Dashboard');
  }, []);

  useEffect(() => {
    sendMixpanelEvent(Event.ORG_DASHBOARD_HOME_PAGE_VIEWED, {
      org_name: currentOrg?.name,
      user_name: currentUser?.name,
      user_role: currentUser?.role_name,
      user_phone: currentUser?.mobile,
      branchId: selectedBranchId,
      dateRange: selectedDateRange
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstBranchTrack = useRef(true);

  useEffect(() => {
    if (isFirstBranchTrack.current) {
      isFirstBranchTrack.current = false;
      return;
    }
    sendMixpanelEvent(Event.DASHBOARD_SELECTED_BRANCH, {
      branchId: selectedBranchId
    });
  }, [selectedBranchId]);

  useEffect(() => {
    if (isPermissionsFetching) setIsPageLoading(true);

    if (!isPermissionsFetching && isPermissionsSuccess) {
      const hasDashboardViewAccess = checkPermission(
        [UserPermissionTypes.ORG_DASHBOARD_VIEW],
        rolesAndPermissions
      );
      if (hasDashboardViewAccess && !currentOrg?.has_multiple_currencies) {
        setIsPageLoading(false);
      } else {
        navigate(GROUPS.HOME);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermissionsFetching, isPermissionsSuccess]);

  useEffect(() => {
    setIsPageLoading(true);
    getRolesAndPermissions(orgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  return (
    <div className="page bg-cofeeNeutralBg">
      {isPageLoading ? (
        <div className="flex h-[calc(100vh-64px)] w-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <>
          <Head title="Dashboard" />
          <div className="flex flex-wrap items-center justify-between gap-4 lg:flex-nowrap lg:gap-0">
            <div className="text-2xl font-bold text-content">Overview</div>

            <div className="flex items-center gap-2">
              <DashboardDateRangePicker
                handleDateRangeSelect={handleDateRangeSelect}
              />
              <div className="h-4 w-px bg-cofeeNeutral5" />
              <div className="text-sm text-cofeeNeutral10">Based on:</div>
              <Dropdown
                currentFilter={selectedFilter}
                filterOptions={paymentDateFilterOptions}
                onSelectFilter={(filter: string) =>
                  setSelectedFilter(filter as PaymentDateFilterType)
                }
                className="min-w-48"
                selectedOptionClassName="bg-transparent"
                isDividerNeeded={false}
                optionsStyle="!shadow-lg right-0"
                optionItemClassName="hover:!bg-cofeeGreyBlue75 !tracking-normal !leading-5 !h-9 !font-medium !py-1"
              />
            </div>
          </div>

          <DashboardCardsOverview
            organizationId={orgId}
            dateRange={selectedDateRange.dateRange}
            showGrowth={
              selectedDateRange.dateRangeType !==
              DashboardDateRangePickerTypesEnum.custom
            }
            selectedBranchId={
              selectedBranchId === allBranches.id ? '' : selectedBranchId
            }
            isClickable={false}
            selectedFilter={selectedFilter}
          />

          <div className="mt-4 flex flex-col gap-4">
            <div className="flex flex-wrap gap-4 lg:flex-nowrap">
              <BranchRevenueChart
                selectedBranch={selectedBranch}
                selectedDateRange={selectedDateRange.dateRange}
                selectedBranchId={selectedBranchId}
                selectedFilter={selectedFilter}
              />
              <GroupPerfomanceChart
                selectedDateRange={selectedDateRange.dateRange}
                selectedBranchId={
                  selectedBranchId === allBranches.id ? '' : selectedBranchId
                }
                selectedFilter={selectedFilter}
              />
            </div>
            <PaymentLinksAndModes
              selectedDateRange={selectedDateRange.dateRange}
              selectedBranchId={
                selectedBranchId === allBranches.id ? '' : selectedBranchId
              }
              selectedFilter={selectedFilter}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default OrgDashboard;
